import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpParams,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, of } from 'rxjs';
import { delay, map } from 'rxjs/operators';
import { environment } from 'src/environments/environment';
import { DemoModeService } from 'src/app/pages/demo/demo-mode.service';
import { sampleMetrics } from 'src/app/shared/sample-data/sample-company';
import {
  sampleCarbonFootprintSaves,
  sampleIncentiveGroups,
  sampleIncentiveRecipients,
  sampleIncentivesSummary,
  sampleOfficeSubsidyConfigs,
  sampleSubsidyConfigs,
} from 'src/app/shared/sample-data/sample-carpooling';
import {
  SAMPLE_RIDE_BOOKING_RULES,
  filterSampleRideBookings,
  filterSampleRideReviews,
  filterSampleRides,
  sampleCorporateRides,
  sampleRideBookingReceipt,
  sampleRideBookings,
  sampleRideGroupEmployees,
  sampleRideGroups,
  sampleRideKpis,
  sampleRideReviewKpis,
  sampleRideReviews,
} from 'src/app/shared/sample-data/sample-taxi-vtc';
import type { CorporateRidePeriod } from 'src/app/models/corporation-ride.interface';
import { madridDay } from 'src/app/utils/madrid-day';

/** Lista del back (`{ data, total, filtered }`) con las filas que deja pasar `keep`. */
function filterRows(body: any, keep: (row: any) => boolean): any {
  const data = (body?.data ?? []).filter(keep);
  return { ...body, data, total: data.length, filtered: data.length };
}

/**
 * Lecturas del back con datos fijos en el dashboard de ejemplo, en
 * `src/assets/demo/`. La ruta se compara sin el prefijo de la API
 * (`/backoffice/...`) y sin la query. `filter` recorta el fichero según la
 * query, como lo haría el back (los empleados, por `corporation_office_id`).
 */
export const DEMO_FIXTURES: { match: RegExp; file: string; filter?: (body: any, params: HttpParams) => any }[] = [
  { match: /^\/backoffice\/corporation-offices$/, file: 'offices.json' },
  { match: /^\/backoffice\/corporation-offices\/[^/]+$/, file: 'office.json' },
  { match: /^\/backoffice\/corporations\/[^/]+$/, file: 'corporation.json' },
  {
    match: /^\/backoffice\/corporation-users$/,
    file: 'employees.json',
    filter: (body, params) => {
      const officeId = params.get('corporation_office_id');
      return officeId ? filterRows(body, (row) => row?.office?.id === officeId) : body;
    },
  },
  { match: /^\/backoffice\/corporation-users\/import-template$/, file: 'plantilla-empleados.csv' },
];

/** Fin del plazo del PMST de «WAIIS S.L.». */
export const DEMO_PMST_DEADLINE = '2027-06-30';

/**
 * Una lectura calculada: `params` junta los de la petición y los que el
 * servicio dejó escritos en la URL (Subvención); `path` es la ruta sin la API
 * ni la query, para sacar de ella los ids.
 */
export interface DemoComputed {
  match: RegExp;
  body: (params: HttpParams, now: Date, path: string) => unknown;
}

/** Lista paginada del back (`{ data, meta }`) con la página y el tamaño pedidos. */
function paginate<T>(rows: T[], params: HttpParams, perPageDefault = 15): { data: T[]; meta: { current_page: number; last_page: number; total: number } } {
  const perPage = Number(params.get('per_page')) || perPageDefault;
  const lastPage = Math.max(1, Math.ceil(rows.length / perPage));
  const page = Math.min(Math.max(1, Number(params.get('page')) || 1), lastPage);
  return {
    data: rows.slice((page - 1) * perPage, page * perPage),
    meta: { current_page: page, last_page: lastPage, total: rows.length },
  };
}

// ─── Carpooling: Sostenibilidad, Incentivos y Subvención ─────────────────────
// Cifras de `sample-data/sample-carpooling.ts`; la sede llega en `office_id`.

const CARPOOLING_COMPUTED: DemoComputed[] = [
  {
    match: /^\/backoffice\/dashboard-statistics\/corporations-carbon-footprint-saves$/,
    body: (params, now) =>
      sampleCarbonFootprintSaves({
        officeId: params.get('office_id'),
        dateStart: params.get('date_start') ?? isoMonthStart(now),
        now,
      }),
  },
  // Sin `data`: el resumen llega suelto.
  { match: /^\/backoffice\/incentives\/summary$/, body: (params) => sampleIncentivesSummary(params.get('office_id')) },
  {
    match: /^\/backoffice\/incentives\/campaign-groups$/,
    body: (params, now) => paginate(sampleIncentiveGroups(params.get('office_id'), now), params),
  },
  {
    match: /^\/backoffice\/incentives\/campaign-groups\/[^/]+$/,
    body: (params, now, path) => {
      const id = path.split('/').pop();
      const officeId = params.get('office_id');
      const group =
        sampleIncentiveGroups(officeId, now).find((g) => g.id === id) ??
        sampleIncentiveGroups(null, now).find((g) => g.id === id) ??
        null;
      return { data: group };
    },
  },
  {
    match: /^\/backoffice\/incentives\/campaigns\/[^/]+\/recipients$/,
    body: (params, now, path) =>
      paginate(sampleIncentiveRecipients(path.split('/')[4], params.get('office_id'), now), params, 25),
  },
  // Lista suelta (sin `data`): la pantalla busca la activa con `configs.find`.
  { match: /^\/backoffice\/corporation-subsidy-pricing-configs$/, body: () => sampleSubsidyConfigs() },
  { match: /^\/backoffice\/corporation-office-subsidy-pricing-configs$/, body: () => sampleOfficeSubsidyConfigs() },
];

// ─── Taxi / VTC: Viajes y gasto, Reservas y Revisión ─────────────────────────
// Cifras de `sample-data/sample-taxi-vtc.ts`; la empresa va en la ruta
// (`corporations/{id}/…`) y estas listas son las de Laravel, sin `meta`.

/** Lista paginada de Laravel (`{ data, current_page, per_page, total, last_page }`). */
function laravelPage<T>(rows: T[], params: HttpParams, perPageDefault: number) {
  const { data, meta } = paginate(rows, params, perPageDefault);
  const perPage = Number(params.get('per_page')) || perPageDefault;
  return { data, current_page: meta.current_page, per_page: perPage, total: meta.total, last_page: meta.last_page };
}

/** Último segmento de la ruta (el id de un viaje o de una reserva). */
const lastSegment = (path: string, fromEnd = 1) => path.split('/').slice(-fromEnd)[0];

const TAXI_VTC_COMPUTED: DemoComputed[] = [
  {
    match: /^\/backoffice\/corporations\/[^/]+\/ride-kpis$/,
    body: (params, now) => ({
      data: sampleRideKpis((params.get('period') as CorporateRidePeriod) ?? 'last_30_days', params.get('corporation_office_id'), now),
    }),
  },
  {
    match: /^\/backoffice\/corporations\/[^/]+\/rides$/,
    body: (params, now) =>
      laravelPage(
        filterSampleRides(sampleCorporateRides(now), {
          from: params.get('from'),
          to: params.get('to'),
          officeId: params.get('corporation_office_id'),
          stateGroup: params.get('state_group'),
          reviewDecision: params.get('review_decision'),
          reviewLevel: params.get('review_level'),
          q: params.get('q'),
        }),
        params,
        25,
      ),
  },
  // Sin el viaje (otro día de Madrid, id inventado) va `data: null` y la pantalla dice que no se ha podido cargar.
  { match: /^\/backoffice\/corporations\/[^/]+\/rides\/[^/]+$/, body: (_p, now, path) => ({ data: sampleCorporateRides(now).find((r) => r.id === lastSegment(path)) ?? null }) },
  {
    match: /^\/backoffice\/corporations\/[^/]+\/ride-bookings$/,
    body: (params, now) =>
      laravelPage(
        filterSampleRideBookings(sampleRideBookings(now), {
          status: params.get('status'),
          from: params.get('from'),
          to: params.get('to'),
          officeId: params.get('office_id'),
          sort: params.get('sort'),
        }),
        params,
        25,
      ),
  },
  // Antes que el detalle: `rules` también es `ride-bookings/{algo}`.
  { match: /^\/backoffice\/corporations\/[^/]+\/ride-bookings\/rules$/, body: () => ({ data: SAMPLE_RIDE_BOOKING_RULES }) },
  {
    match: /^\/backoffice\/corporations\/[^/]+\/ride-bookings\/[^/]+\/receipt$/,
    body: (_p, now, path) => {
      const booking = sampleRideBookings(now).find((b) => b.id === lastSegment(path, 2));
      return { data: booking ? sampleRideBookingReceipt(booking) : null };
    },
  },
  { match: /^\/backoffice\/corporations\/[^/]+\/ride-bookings\/[^/]+$/, body: (_p, now, path) => ({ data: sampleRideBookings(now).find((b) => b.id === lastSegment(path)) ?? null }) },
  // Grupos y empleados de la política: el detalle de una reserva pone nombre a los pasajeros.
  { match: /^\/backoffice\/corporations\/[^/]+\/ride-groups$/, body: () => ({ data: sampleRideGroups() }) },
  { match: /^\/backoffice\/corporations\/[^/]+\/ride-group-employees$/, body: () => ({ data: sampleRideGroupEmployees() }) },
  {
    match: /^\/backoffice\/corporations\/[^/]+\/ride-reviews$/,
    body: (params, now) => {
      const rows = filterSampleRideReviews(sampleRideReviews(now), params.get('status'), params.get('level'));
      const { data, current_page, last_page, total } = laravelPage(rows, params, 10);
      return { data, current_page, last_page, total };
    },
  },
  // Antes que el detalle: `kpis` también es `ride-reviews/{algo}`.
  { match: /^\/backoffice\/corporations\/[^/]+\/ride-reviews\/kpis$/, body: (_p, now) => ({ data: sampleRideReviewKpis(now) }) },
  { match: /^\/backoffice\/corporations\/[^/]+\/ride-reviews\/[^/]+$/, body: (_p, now, path) => ({ data: sampleRideReviews(now).find((r) => r.ride_id === lastSegment(path)) ?? null }) },
];

/**
 * Lecturas del ejemplo que se calculan en vez de leerse de un fichero: las
 * cifras salen de la misma fuente que el resto de pantallas
 * (`sampleMetrics`), y los días del plazo, del día de Madrid en que se mira.
 * Cada bloque de páginas añade las suyas al final (`CARPOOLING_COMPUTED`…).
 */
export const DEMO_COMPUTED: DemoComputed[] = [
  // Antes que la de la empresa: `corporations/kpi` también es `corporations/{algo}`.
  {
    match: /^\/backoffice\/corporations\/kpi$/,
    body: (params) => {
      const m = sampleMetrics({ officeId: params.get('corporation_office_id') });
      return {
        data: {
          total_employees: m.declared,
          registered_count: m.registered,
          registered_percentage: m.registeredPercent,
          linked_count: m.linked,
          active_users_count: m.activePeriod,
        },
      };
    },
  },
  {
    match: /^\/backoffice\/corporation-sustainability-deadline$/,
    body: (_params, now) => ({
      data: { deadline: DEMO_PMST_DEADLINE, days_remaining: daysBetween(madridDay(now), DEMO_PMST_DEADLINE) },
    }),
  },
  ...CARPOOLING_COMPUTED,
  ...TAXI_VTC_COMPUTED,
];

/** Primer día (`Y-m-d`) del mes de `now`. */
function isoMonthStart(now: Date): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
}

/** Los parámetros de la petición más los que vienen escritos en la URL (`?corporation_id=…`). */
function requestParams(req: HttpRequest<unknown>): HttpParams {
  const query = req.url.split('?')[1];
  if (!query) return req.params;
  let params = new HttpParams({ fromString: query });
  for (const key of req.params.keys()) {
    for (const value of req.params.getAll(key) ?? []) params = params.append(key, value);
  }
  return params;
}

/** Días entre dos días `Y-m-d` (negativo si `to` ya pasó). */
function daysBetween(from: string, to: string): number {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86_400_000);
}

/** Lo que recibe cualquier otra lectura: listas vacías y totales a cero. */
const EMPTY_BODY = { data: [], total: 0, recordsTotal: 0, recordsFiltered: 0 };

/**
 * Dashboard de ejemplo sin registro (#20261023-090014): mientras
 * `DemoModeService` está activo, ninguna petición sale hacia el back. Las
 * lecturas conocidas se responden con los datos fijos de «WAIIS S.L.»
 * (`assets/demo/*.json`), las demás con una respuesta vacía (las páginas del
 * ejemplo pintan sus datos de ejemplo con `MockModeService`), y cualquier
 * escritura se corta con el aviso «Para guardar, crea tu espacio» sin
 * responder, para que la pantalla no saque además su propio error.
 *
 * Va el primero de la cadena: antes del token y de los avisos de error.
 */
@Injectable()
export class DemoInterceptor implements HttpInterceptor {
  constructor(private demo: DemoModeService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (!this.demo.active || !req.url.startsWith(environment.laravelApi)) return next.handle(req);

    if (req.method !== 'GET') {
      this.demo.blockWrite();
      return EMPTY;
    }

    const path = req.url.slice(environment.laravelApi.length).split('?')[0];
    const computed = DEMO_COMPUTED.find((c) => c.match.test(path));
    if (computed) return respondLater(req, computed.body(requestParams(req), new Date(), path));

    const fixture = DEMO_FIXTURES.find((f) => f.match.test(path));
    if (fixture) {
      const file$ = next.handle(req.clone({ url: `assets/demo/${fixture.file}`, params: new HttpParams() }));
      const filter = fixture.filter;
      if (!filter) return file$;
      return file$.pipe(
        map((event) => (event instanceof HttpResponse ? event.clone({ body: filter(event.body, req.params) }) : event)),
      );
    }
    return respondLater(req, EMPTY_BODY);
  }
}

/**
 * Asíncrona, como una respuesta de verdad: las páginas se suscriben al modo
 * de ejemplo justo después de pedir sus datos (Funnel se quedaba «Cargando…»).
 */
function respondLater(req: HttpRequest<unknown>, body: unknown): Observable<HttpEvent<unknown>> {
  return of(new HttpResponse({ status: 200, url: req.url, body })).pipe(delay(0));
}
